import { useEffect } from 'react';
import './FocusBlastAnimation.css';

/**
 * PROFESSIONAL FOCUS BLAST ANIMATION
 * Fighting-type special move - hurls a concentrated ball of fighting energy
 * 
 * Key Features:
 * - Fighting-type special attack (120 base power, 70% accuracy)
 * - 10% chance to lower Sp. Def
 * - Massive energy orb projectile with explosive impact
 * - Professional gaming quality with intense fighting aura
 */
export default function FocusBlastAnimation({ playerRef, opponentRef, onComplete }) {
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    const container = playerEl.closest('.battle-screen') || playerEl.parentElement || document.body;
    const containerRect = container.getBoundingClientRect();

    const playerRect = playerEl.getBoundingClientRect();
    const opponentRect = opponentEl.getBoundingClientRect();

    const playerX = Math.round((playerRect.left + playerRect.width / 2) - containerRect.left);
    const playerY = Math.round((playerRect.top + playerRect.height / 2) - containerRect.top);
    const opponentX = Math.round((opponentRect.left + opponentRect.width / 2) - containerRect.left);
    const opponentY = Math.round((opponentRect.top + opponentRect.height / 2) - containerRect.top);

    const dx = opponentX - playerX;
    const dy = opponentY - playerY;

    const elements = [];

    // PHASE 1: Focus Energy Buildup (0-600ms)
    playerEl.classList.add('focus-blast-user-charge');

    const focusAura = document.createElement('div');
    focusAura.className = 'focus-blast-aura';
    focusAura.style.left = `${playerX - 80}px`;
    focusAura.style.top = `${playerY - 80}px`;
    container.appendChild(focusAura);
    elements.push(focusAura);

    // Fighting energy orb forming
    const formingOrb = document.createElement('div');
    formingOrb.className = 'focus-blast-forming-orb';
    formingOrb.style.left = `${playerX - 22}px`;
    formingOrb.style.top = `${playerY - 22}px`;
    container.appendChild(formingOrb);
    elements.push(formingOrb);

    // Energy gathering particles
    for (let i = 0; i < 14; i++) {
      const energy = document.createElement('div');
      energy.className = 'focus-blast-energy-gather';
      const angle = (i / 14) * 360;
      const radius = 70;
      energy.style.left = `${playerX + Math.cos(angle * Math.PI / 180) * radius - 4}px`;
      energy.style.top = `${playerY + Math.sin(angle * Math.PI / 180) * radius - 4}px`;
      energy.style.animationDelay = `${i * 35}ms`;
      energy.style.setProperty('--target-x', `${playerX - (playerX + Math.cos(angle * Math.PI / 180) * radius)}px`);
      energy.style.setProperty('--target-y', `${playerY - (playerY + Math.sin(angle * Math.PI / 180) * radius)}px`);
      container.appendChild(energy);
      elements.push(energy);
    }

    // PHASE 2: Focus Blast Launch (600-1100ms)
    const phase2Timer = setTimeout(() => {
      if (formingOrb.parentNode) formingOrb.remove();

      // Main focus blast projectile
      const blast = document.createElement('div');
      blast.className = 'focus-blast-projectile';
      blast.style.left = `${playerX}px`;
      blast.style.top = `${playerY}px`;
      blast.style.setProperty('--dx', `${dx}px`);
      blast.style.setProperty('--dy', `${dy}px`);
      container.appendChild(blast);
      elements.push(blast);

      // Energy trail
      for (let i = 0; i < 16; i++) {
        const trailTimer = setTimeout(() => {
          const trail = document.createElement('div');
          trail.className = 'focus-blast-trail';
          const progress = i / 16;
          trail.style.left = `${playerX + dx * progress + (Math.random() - 0.5) * 20 - 8}px`;
          trail.style.top = `${playerY + dy * progress + (Math.random() - 0.5) * 20 - 8}px`;
          container.appendChild(trail);
          elements.push(trail);
        }, i * 28);
        elements.push({ timer: trailTimer, type: 'timer' });
      }
    }, 600);
    elements.push({ timer: phase2Timer, type: 'timer' });

    // PHASE 3: Explosive Impact (1100-1600ms)
    const phase3Timer = setTimeout(() => {
      opponentEl.classList.add('focus-blast-opponent-hit');

      // Massive explosion
      const explosion = document.createElement('div');
      explosion.className = 'focus-blast-explosion';
      explosion.style.left = `${opponentX - 120}px`;
      explosion.style.top = `${opponentY - 120}px`;
      container.appendChild(explosion);
      elements.push(explosion);

      // Energy shockwaves
      for (let i = 0; i < 4; i++) {
        const swTimer = setTimeout(() => {
          const sw = document.createElement('div');
          sw.className = 'focus-blast-shockwave';
          sw.style.left = `${opponentX - 80}px`;
          sw.style.top = `${opponentY - 80}px`;
          container.appendChild(sw);
          elements.push(sw);
        }, i * 100);
        elements.push({ timer: swTimer, type: 'timer' });
      }

      // Burst particles
      for (let i = 0; i < 22; i++) {
        const burstAngle = (i / 22) * 360;
        const burstDist = 60 + Math.random() * 55;
        const burst = document.createElement('div');
        burst.className = 'focus-blast-burst';
        burst.style.left = `${opponentX - 5}px`;
        burst.style.top = `${opponentY - 5}px`;
        burst.style.setProperty('--scatter-x', `${Math.cos(burstAngle * Math.PI / 180) * burstDist}px`);
        burst.style.setProperty('--scatter-y', `${Math.sin(burstAngle * Math.PI / 180) * burstDist}px`);
        burst.style.animationDelay = `${i * 12}ms`;
        container.appendChild(burst);
        elements.push(burst);
      }
    }, 1100);
    elements.push({ timer: phase3Timer, type: 'timer' });

    // PHASE 4: Aftermath (1600-2000ms)
    const phase4Timer = setTimeout(() => {
      playerEl.classList.remove('focus-blast-user-charge');

      for (let i = 0; i < 6; i++) {
        const linger = document.createElement('div');
        linger.className = 'focus-blast-linger';
        linger.style.left = `${opponentX + (Math.random() - 0.5) * 80 - 8}px`;
        linger.style.top = `${opponentY + (Math.random() - 0.5) * 50 - 8}px`;
        linger.style.animationDelay = `${i * 40}ms`;
        container.appendChild(linger);
        elements.push(linger);
      }
    }, 1600);
    elements.push({ timer: phase4Timer, type: 'timer' });

    // CLEANUP (2000ms)
    const cleanupTimer = setTimeout(() => {
      playerEl.classList.remove('focus-blast-user-charge');
      opponentEl.classList.remove('focus-blast-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') clearTimeout(item.timer);
        else if (item && item.parentNode) item.remove();
      });
      onComplete();
    }, 2000);

    return () => {
      clearTimeout(cleanupTimer);
      playerEl.classList.remove('focus-blast-user-charge');
      opponentEl.classList.remove('focus-blast-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') clearTimeout(item.timer);
        else if (item && item.parentNode) item.remove();
      });
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

FocusBlastAnimation.moveType = 'fighting';
